import { CustomTippyWrapper, Wrapper } from './styles'
import RadioInput from 'components/shared/Radio/input'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import InfoTooltip from 'components/shared/InfoTooltip'
import { KeyboardEvent } from 'react'
import { useIntl } from 'react-intl'
import { useResolutions } from 'store/resolutions'
import { DefaultTheme } from 'styled-components'
import { Color, TextVariant } from 'styles/types'

type TextStylesType = {
    titleVariant: TextVariant
    titleColor: Color
    infoVariant: TextVariant
    infoColor: Color
}

interface PersonTypeRadioProps {
    option: string
    id: string
    isChecked: boolean
    label: string
    info?: string | null
    onClick: () => void
    displayName?: string
    theme?: DefaultTheme
    testId?: string
}

const PersonTypeRadio = ({
    option,
    id,
    isChecked,
    label,
    info,
    onClick,
    displayName,
    theme,
    testId,
}: PersonTypeRadioProps) => {
    const { formatMessage } = useIntl()
    const { isMobile } = useResolutions()

    const hasDarkMode = theme?.mode === 'dark'

    const textStyles: TextStylesType = isChecked
        ? {
              titleVariant: 'bodySmallStrong',
              titleColor: 'grayscale_900',
              infoVariant: 'bodySmallStrong',
              infoColor: 'primary_500',
          }
        : {
              titleVariant: 'bodySmall',
              titleColor: 'grayscale_900',
              infoVariant: 'bodySmall',
              infoColor: hasDarkMode ? 'primary_500' : 'secondary_500',
          }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) =>
        event.key === 'Enter' && onClick()

    const discountPersonsList = (
        <Box as="span" ml={8} direction="column">
            <Text as="span" variant="bodySmall" color="grayscale_0">
                &bull;{' '}
                {formatMessage({
                    id: 'annual.config.person_type_tooltip_discount_pregnant',
                    defaultMessage: 'osobom w ciąży,',
                })}
            </Text>
            <Text as="span" variant="bodySmall" color="grayscale_0">
                &bull;{' '}
                {formatMessage({
                    id: 'annual.config.person_type_tooltip_discount_disabilities',
                    defaultMessage: 'osobom z niepełnosprawnościami,',
                })}
            </Text>
            <Text as="span" variant="bodySmall" color="grayscale_0">
                &bull;{' '}
                {formatMessage({
                    id: 'annual.config.person_type_tooltip_discount_old',
                    defaultMessage: 'osobom w wieku 65 lat i starszym.',
                })}
            </Text>
        </Box>
    )
    const discountPersonsListMobile = (
        <Box as="span" ml={8} direction="column">
            <Text
                as="span"
                variant={textStyles.infoVariant}
                color={textStyles.infoColor}
            >
                &bull;{' '}
                {formatMessage({
                    id: 'annual.config.person_type_tooltip_discount_pregnant_mobile',
                    defaultMessage: 'Kobiety w ciąży',
                })}
            </Text>
            <Text
                as="span"
                variant={textStyles.infoVariant}
                color={textStyles.infoColor}
            >
                &bull;{' '}
                {formatMessage({
                    id: 'annual.config.person_type_tooltip_discount_disabilities_mobile',
                    defaultMessage: 'Osoby z niepełnosprawnościami',
                })}
            </Text>
            <Text
                as="span"
                variant={textStyles.infoVariant}
                color={textStyles.infoColor}
            >
                &bull;{' '}
                {formatMessage({
                    id: 'annual.config.person_type_tooltip_discount_old_mobile',
                    defaultMessage: 'Osoby w wieku 65 lat i starsze',
                })}
            </Text>
        </Box>
    )

    return (
        <Wrapper
            direction={['row', 'column-reverse']}
            align="center"
            justify={['flex-start', 'space-between']}
            gap={[12, 0]}
            isChecked={isChecked}
            onClick={onClick}
            theme={theme}
            data-testid={testId}
            onKeyDown={handleKeyDown}
        >
            <RadioInput
                option={option}
                id={id}
                isChecked={isChecked}
                name={testId}
            />
            <Box align={['flex-start', 'center']} justify="center">
                <Box
                    align={['flex-start', 'center']}
                    justify="center"
                    direction="row"
                >
                    <Text
                        variant={textStyles.titleVariant}
                        color={
                            hasDarkMode
                                ? 'grayscale_200'
                                : textStyles.titleColor
                        }
                        align="center"
                        dangerouslySetInnerHTML={{
                            __html: displayName ? displayName : label,
                        }}
                        mr={id === 'discounted' ? 8 : 0}
                    />
                    {id === 'discounted' && !isMobile && (
                        <CustomTippyWrapper isLarge>
                            <InfoTooltip
                                isLarge
                                message={
                                    <>
                                        <Text
                                            as="span"
                                            variant="bodySmall"
                                            color="grayscale_0"
                                        >
                                            {formatMessage({
                                                id: 'annual.config.person_type_tooltip_discount_title',
                                                defaultMessage:
                                                    'Ulga przysługuje:',
                                            })}
                                        </Text>
                                        {discountPersonsList}
                                    </>
                                }
                                size={22}
                            />
                        </CustomTippyWrapper>
                    )}
                </Box>

                {!!info && (
                    <Text
                        variant={textStyles.infoVariant}
                        color={textStyles.infoColor}
                    >
                        {info}
                    </Text>
                )}
                {id === 'discounted' && isMobile && discountPersonsListMobile}
            </Box>
        </Wrapper>
    )
}

export default PersonTypeRadio
